import qs from "query-string";
import { useCallback, useEffect, useState } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";

const OPENED_MODAL_KEY = "openedModal";

export type UseModalParams = {
    name: string;
    defaultIsOpen?: boolean;
    useUrl?: boolean;
    openedModalKey?: string;
    additionalUrlKeys?: { key: string; value: string }[];
};

export const useModal = (params: UseModalParams): [boolean, (isOpen?: boolean, newLocation?: Location) => void] => {
    const { name, defaultIsOpen, useUrl = true, openedModalKey = OPENED_MODAL_KEY, additionalUrlKeys = [] } = params;
    const navigate = useNavigate();
    const location = useLocation();

    const [isOpen, setIsOpen] = useState<boolean>(defaultIsOpen || false);

    const formatAdditionalUrlKeys = useCallback(
        (additionalUrlKeys: { key: string; value: string }[]) =>
            additionalUrlKeys.reduce<Record<string, string>>((acc, key) => {
                acc[key.key] = key.value;
                return acc;
            }, {}),
        [],
    );

    const toggle = useCallback(
        async (newIsOpen = !isOpen, newLocation?: Location): Promise<void> => {
            const locationToUsed = newLocation || location;

            if (useUrl) {
                const parsedQuery = qs.parse(locationToUsed.search);
                const currentModalInUrl = parsedQuery[openedModalKey];

                // Si on ferme la modal, vérifier que c'est bien cette modal qui est ouvert
                // Ne pas supprimer le paramètre openedModal de l'url si c'est une AUTRE modal qui est ouvert
                if (!newIsOpen && currentModalInUrl !== name) {
                    setIsOpen(false);
                    return;
                }

                navigate({
                    ...locationToUsed,
                    search: qs.stringify({
                        ...parsedQuery,
                        [openedModalKey]: newIsOpen ? name : undefined,
                        ...formatAdditionalUrlKeys(additionalUrlKeys),
                    }),
                });
            } else {
                setIsOpen(newIsOpen);
            }
        },
        [location],
    );
    useEffect(() => {
        const parsedQuery = qs.parse(location.search);
        if (defaultIsOpen || (defaultIsOpen === undefined && parsedQuery[openedModalKey] === name)) {
            toggle(true);
        } else if (parsedQuery[openedModalKey] === name && defaultIsOpen === false) {
            navigate(
                {
                    ...location,
                    search: qs.stringify({
                        ...parsedQuery,
                        [openedModalKey]: undefined,
                    }),
                },
                { replace: true },
            );
        }
    }, [defaultIsOpen]);

    useEffect(() => {
        if (useUrl) {
            const parsedQuery = qs.parse(location.search);
            setIsOpen(parsedQuery[openedModalKey] === name);
        }
    }, [useUrl, location.search]);

    return [isOpen, toggle];
};
